import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {combineLatest, Observable, of, Subject, switchMap} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  take,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';
import {
  selectActiveAchIncomingInstructions,
  selectActiveOnRequestAchInstructions,
  selectActiveOnRequestAndRecurringAchInstructions,
  selectAreAccountAssetsPledged,
  selectEAuthCustomers,
  selectIsIip,
  selectIsPtrs,
  selectSubAccountId, selectTemplatesListEmpty
} from '../../../account-info-header/reducers';
import {selectRequestedDate, selectStandingAuthTableStartTransactionClickedACH} from '../../../shared/reducers';
import {ComponentState} from '../../../shared/types/ComponentState';
import {StandingInstruction} from '../../../shared/types/StandingInstruction';
import {AchActions, PtrsActions, StandingAuthorizationsTableActions, TransactionFormActions} from '../../actions';
import {AchExtraPrompts} from '../../models/form/TransactionForm';
import {
  selectAchInputFinished,
  selectIsFromEdit, selectRequestingAccountHolderId,
  selectStandingAuthCallSucceededAch,
  selectStandingAuthId,
  selectTransactionForm,
  selectTransactionFormDirection
} from '../../reducers';
import {buildFromPrefilledForm} from '../../utilities/formUtilities';
import {RadioControl} from '../../models/form/FormControlRadio';
import {Customers} from "../../../account-info-header/models/AccountInfoApiResponse";
import {selectIsAchPathEnabled} from "../../../feature-flags/reducers";


@Component({
  standalone: false,
  selector: 'mm-ach-extra-prompts',
  templateUrl: './ach-extra-prompts.component.html',
  styleUrls: ['./ach-extra-prompts.component.scss']  
})
export class AchExtraPromptsComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() prefilledPrompts: AchExtraPrompts | undefined;
  @Input() prefilledPromptsObs: Observable<AchExtraPrompts | null>;
  @Input() isInitiateAndRelease: boolean;
  @Output() extraPromptsComplete = new EventEmitter<boolean>();
  @Output() extraPromptsUpdatedInEdit = new EventEmitter();

  showTargetOfFunds: boolean;
  instructions$: Observable<StandingInstruction[]>;
  directionSelection$: Observable<'incoming' | 'outgoing' | ''>;
  customers$: Observable<Customers[]>;
  isPtrs: boolean;
  isIip: boolean;
  subAccountId: string;
  isFromEdit: boolean = false;
  validInstructionsForIipAccount: boolean = true;
  private readonly controlNames = {
    standingAuthId: 'standingAuthId',
    transferDirection: 'transferDirection',
    frequency: 'frequency'
  }
  private _memoizedTransferDirection: 'incoming' | 'outgoing' | '';
  private destroy$ = new Subject<void>();
  formState$: Observable<ComponentState>;
  frequencySelectorModel: RadioControl = {
    title: 'Frequency',
    controlName: 'frequency',
    radios: [
      {
        value: 'onetime',
        label: ['One-time'],
      },
    ],
    validationMessage: 'Select a frequency'
  };
  showFrequencySelection: boolean;
  assetsArePledged: boolean;
  achPathEnabled$: Observable<boolean>;
  achPathEnabled: boolean;
  showRequestingAccountHolder: boolean = false;
  requestingAccountHolderCount: number = 0;

  constructor(private fb: UntypedFormBuilder, private store: Store) {
  }

  ngOnInit(): void {
    const achFormGroup = this.fb.group({
        [this.controlNames.transferDirection]: ['', this.getControlValidators(this.controlNames.transferDirection)],
      },
      {
        validators: validateDisabledFieldsInGroup()
      });

    this.parentForm.setControl('extraPrompts', achFormGroup);

    // We use take(2) to grab first 2 values from the values that the selector emits, one for true and one for false
    this.formState$ = this.store.select(selectTransactionForm).pipe(take(2));

    this.store.select(selectIsIip).pipe(takeUntil(this.destroy$)).subscribe(isIip => {
      this.isIip = isIip
    });

    this.store.select(selectAreAccountAssetsPledged).pipe(takeUntil(this.destroy$)).subscribe(arePledged => this.assetsArePledged = arePledged)

    combineLatest([
      this.store.pipe(select(selectStandingAuthCallSucceededAch)),
      this.store.pipe(select(selectActiveAchIncomingInstructions)),
      this.transferDirectionControl.statusChanges
    ]).pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(([callFinished, incomingInstructions, status]) => {
      const validControl = status === 'VALID' || status === 'DISABLED';
      if (validControl) {
        const controlName = this.controlNames.standingAuthId;
        if (!achFormGroup.contains(controlName)) {
          achFormGroup.setControl(controlName, new UntypedFormControl('', this.getControlValidators(controlName)));
        }
      }

      if (this.isIip && incomingInstructions.length === 0) {
        this.validInstructionsForIipAccount = false;
      }

      if (callFinished && validControl && this.validInstructionsForIipAccount) {
        this.showTargetOfFunds = true;
      }
    })

    this.achPathEnabled$ = this.store.select(selectIsAchPathEnabled);
    this.achPathEnabled$.pipe(takeUntil(this.destroy$)).subscribe((enabled) => {
      this.achPathEnabled = enabled;
    })

    if (!this.achPathEnabled) {
      this.store.select(selectStandingAuthId).pipe(takeUntil(this.destroy$)).subscribe(_standingAuthId => {
        if (this.extraPromptsGroup?.contains('frequency')) {
          this.extraPromptsGroup.removeControl('frequency');
          this.showFrequencySelection = false;
        }
      })
    }

    if (this.achPathEnabled) {
      this.store.select(selectStandingAuthId).pipe(takeUntil(this.destroy$)).subscribe(_standingAuthId => {
        if (this.showRequestingAccountHolder) {
          this.showRequestingAccountHolder = false;
          if (this.extraPromptsGroup?.contains('frequency')) {
            this.extraPromptsGroup.removeControl('frequency');
            this.showFrequencySelection = false;
          }
        }
      });
    }

    this.transferDirectionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(direction => {
      // reset source of funds component only when the value has actually changed (prefilled value
      // loading can overwrite the value auto-setting when only incoming/outgoing profiles are present)
      if (this._memoizedTransferDirection !== direction) {
        this._memoizedTransferDirection = direction;
        achFormGroup.get(this.controlNames.standingAuthId)?.reset('');
        this.extraPromptsGroup.removeControl('frequency');
        this.showFrequencySelection = false;
      }

      this.store.dispatch(TransactionFormActions.initializeServiceError());
    })

    // don't emit until the last prompt has been answered and the form status has changed
    this.extraPromptsGroup?.statusChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
      ).subscribe((val) => this.extraPromptsComplete.emit(val === 'VALID'));

    this.directionSelection$ = this.store.pipe(select(selectTransactionFormDirection));

    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$)).subscribe(isPtrs => {
      this.isPtrs = isPtrs
    })

    this.setFrequencyOptions();

    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => {
      this.subAccountId = subAccountId
    })

    if (this.assetsArePledged || this.isInitiateAndRelease) {
      this.instructions$ = this.store.select(selectActiveOnRequestAchInstructions)
    } else {
      this.instructions$ = this.store.select(selectActiveOnRequestAndRecurringAchInstructions);
    }

    this.store.pipe(
      takeUntil(this.destroy$),
      select(selectIsFromEdit)
    ).subscribe(isFromEdit => this.isFromEdit = isFromEdit);

    this.store.select(selectStandingAuthId).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectStandingAuthTableStartTransactionClickedACH)),
      filter(([id, _startTransactionClicked]) => id !== ''),
    ).subscribe(([authorizationUniqueId, startTransactionClicked]) => {
      if (!this.isFromEdit && !startTransactionClicked) {
        this.store.dispatch(AchActions.callAchInput({authorizationUniqueId}))
        if (this.isPtrs) {
          this.store.dispatch(PtrsActions.callPtrs({accountId: this.subAccountId}))
        }
      }
    });

    combineLatest([
      this.prefilledPromptsObs,
      this.store.select(selectStandingAuthTableStartTransactionClickedACH),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([extraPrompts, startTransactionClicked]) => !!extraPrompts && startTransactionClicked),
        map(([extraprompts, _startTransactionClicked]) => extraprompts),
        distinctUntilChanged()
      )
      .subscribe(extraPrompts => {
        if (extraPrompts) {
          buildFromPrefilledForm(extraPrompts, this.extraPromptsGroup);
        }
      });

    if (!this.achPathEnabled) {
      this.store.select(selectAchInputFinished)
        .pipe(
          takeUntil(this.destroy$),
          withLatestFrom(this.store.select(selectIsFromEdit)),
          filter(([inputCallFinished, isFromEdit]) => !isFromEdit && inputCallFinished)
        ).subscribe(([_inputCallFinished, _isFromEdit]) => {
        this.extraPromptsGroup.setControl('frequency', this.fb.control('', Validators.required));
        this.showFrequencySelection = true;
      });

      this.store.select(selectStandingAuthId)
        .pipe(
          takeUntil(this.destroy$),
          withLatestFrom(this.store.select(selectIsFromEdit)),
          filter(([_standingAuthId, isFromEdit]) => isFromEdit)
        ).subscribe(([standingAuthId, _isFromEdit]) => {
        if (standingAuthId) {
          this.showFrequencySelection = true;
        } else {
          this.showFrequencySelection = false;
        }
      })
    }

    if (this.achPathEnabled) {
      this.customers$ = this.store.select(selectEAuthCustomers);

      this.store.select(selectAchInputFinished).pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsFromEdit),
          this.customers$
        ),
        filter(([_, isFromEdit]) => !isFromEdit)
      ).subscribe(([inputCallFinished, _, customers]) => {
        const hasCustomers = customers?.length > 0;

        if (inputCallFinished) {
          if (hasCustomers) {
            this.showRequestingAccountHolder = true;
            this.requestingAccountHolderCount = customers.length;
          }
          // there should always be at least one customer, but if eAuth fails to
          // retrieve any customers, let the user proceed with the rest of the form
          else {
            if (!this.extraPromptsGroup.contains('frequency')) {
              this.extraPromptsGroup.setControl('frequency', this.fb.control('', Validators.required));
            }
            this.showFrequencySelection = true;
          }
        } else if (!hasCustomers) {
          if (this.extraPromptsGroup.contains('frequency')) {
            this.extraPromptsGroup.removeControl('frequency');
          }
          this.showFrequencySelection = false;
        }
      });

      this.store.select(selectRequestingAccountHolderId).pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        withLatestFrom(this.store.select(selectIsFromEdit)),
        filter(([_, isFromEdit]) => !isFromEdit)
      ).subscribe(([requestingAccountHolderId]) => {
        if (requestingAccountHolderId) {
          if (!this.extraPromptsGroup.contains('frequency')) {
            this.extraPromptsGroup.setControl('frequency', this.fb.control('', Validators.required));
          }
          this.showFrequencySelection = true;
        } else {
          if (this.extraPromptsGroup.contains('frequency')) {
            this.extraPromptsGroup.removeControl('frequency');
          }
          this.showFrequencySelection = false;
        }
      });

      this.store.select(selectStandingAuthId)
        .pipe(
          takeUntil(this.destroy$),
          withLatestFrom(this.store.select(selectIsFromEdit),
            this.customers$),
          filter(([_standingAuthId, isFromEdit]) => isFromEdit)
        ).subscribe(([standingAuthId, _isFromEdit, customers]) => {
        if (standingAuthId) {
          this.showRequestingAccountHolder = customers?.length > 0;
          this.requestingAccountHolderCount = customers?.length ?? 0;
          this.showFrequencySelection = true;
        } else {
          this.showFrequencySelection = false;
        }
      })
    }

    /*
      Calling AchInput before standingAuthorization call clears serviceError section in store thus clearing error banner
      Calling AchInput only when standingAuthorization call is successful and startTransaction is clicked
    */
    this.store
      .select(selectStandingAuthCallSucceededAch)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectStandingAuthTableStartTransactionClickedACH),
          this.prefilledPromptsObs
        ),
        filter(
          ([callSucceeded, startTransactionClicked, _extraPrompts]) =>
            callSucceeded && startTransactionClicked
        ),
        map(
          ([_callSucceeded, _startTransactionClicked, extraPrompts]) =>
            extraPrompts
        )
      )
      .subscribe((extraPrompts) => {
        this.showTargetOfFunds = true;

        this.store.dispatch(
          AchActions.callAchInput({
            authorizationUniqueId: extraPrompts?.standingAuthId ?? '',
          })
        );
        if (this.isPtrs) {
          this.store.dispatch(
            PtrsActions.callPtrs({accountId: this.subAccountId})
          );
        }

        this.store.dispatch(
          StandingAuthorizationsTableActions.startTransacionFlowComplete()
        );
      });

    if (this.isFromEdit) {
      buildFromPrefilledForm(
        this.prefilledPrompts,
        this.extraPromptsGroup,
        this.getControlValidators,
        this.extraPromptsUpdatedInEdit
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$?.next();
    this.destroy$?.unsubscribe();
  }

  get extraPromptsGroup(): UntypedFormGroup {
    return this.parentForm?.get('extraPrompts') as UntypedFormGroup;
  }

  get standingAuthIdControl(): UntypedFormControl {
    return this.extraPromptsGroup?.get(
      this.controlNames.standingAuthId
    ) as UntypedFormControl;
  }

  get transferDirectionControl(): UntypedFormControl {
    return this.extraPromptsGroup?.get(
      this.controlNames.transferDirection
    ) as UntypedFormControl;
  }

  getControlValidators(controlName: string): ValidatorFn[] {
    switch (controlName) {
      case 'transferDirection':
        return [Validators.required, Validators.minLength(1)];
      case 'standingAuthId':
        return [Validators.required];
      case 'frequency':
        return [Validators.required];
    }
    return [];
  }

  private setFrequencyOptions(): void {
    if (!this.isPtrs && !this.assetsArePledged) {
      this.frequencySelectorModel.radios.splice(1, 0, {value: 'recurring', label: ['Recurring']});
    }
  }
}

function validateDisabledFieldsInGroup(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const fg = group as UntypedFormGroup;
    const errorMap: ValidationErrors = {};

    const controlNames = Object.keys(fg.controls);
    controlNames.forEach((name) => {
      const control = fg.controls[name];
      if (control?.disabled && typeof control?.validator === 'function') {
        if (Object.keys(control.validator(control) ?? []).length > 0) {
          errorMap.name = true;
        }
      }
    });

    return Object.keys(errorMap).length > 0 ? errorMap : null;
  }

}
